import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Alert,
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  InputBase,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@features/auth/store/authStore';
import { hasPermission } from '@shared/auth/permissions';
import { helixApi } from '@shared/api/helixApi';
import type {
  Conversation,
  IdentityUser,
  KnowledgeArticle,
  LegacyTicketListItem,
} from '@shared/types/domain';
import { useChannelVisibility } from '@shared/channels/channelVisibility';

const SEARCH_DEBOUNCE_MS = 300;
const MAX_RESULTS_PER_GROUP = 5;

type SearchResult = {
  id: string;
  kind: 'agent' | 'ticket' | 'article' | 'whatsapp';
  title: string;
  meta: string;
  location: string;
};

type SearchGroup = {
  key: SearchResult['kind'];
  title: string;
  results: SearchResult[];
};

export function SearchShortcutHint({
  label,
  compact = false,
}: {
  label: string;
  compact?: boolean;
}) {
  return (
    <Box
      component="span"
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.2,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        color: 'text.secondary',
        display: 'inline-flex',
        fontSize: compact ? 10 : 13,
        fontWeight: 900,
        lineHeight: 1,
        px: compact ? 0.55 : 0.85,
        py: compact ? 0.4 : 0.65,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

function resultIcon(kind: SearchResult['kind']) {
  if (kind === 'ticket') return <ConfirmationNumberOutlinedIcon fontSize="small" />;
  if (kind === 'article') return <ArticleOutlinedIcon fontSize="small" />;
  if (kind === 'whatsapp') return <WhatsAppIcon fontSize="small" />;
  return <PersonOutlineRoundedIcon fontSize="small" />;
}

function SearchResultRow({
  result,
  selected,
  onClick,
  onMouseEnter,
}: {
  result: SearchResult;
  selected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}) {
  return (
    <ButtonBase
      aria-selected={selected}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      role="option"
      sx={{
        bgcolor: selected ? 'action.selected' : 'transparent',
        borderRadius: 2,
        display: 'flex',
        gap: 1.6,
        justifyContent: 'flex-start',
        p: 0.75,
        textAlign: 'start',
        width: '100%',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box
        sx={{
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'background.default' : '#f1f3f8'),
          borderRadius: 2,
          color:
            result.kind === 'whatsapp'
              ? 'success.main'
              : result.kind === 'article'
                ? 'primary.main'
                : 'text.secondary',
          display: 'grid',
          flexShrink: 0,
          height: 42,
          placeItems: 'center',
          width: 42,
        }}
      >
        {resultIcon(result.kind)}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ color: 'text.primary', fontSize: 15.5, fontWeight: 800, lineHeight: 1.25 }}
        >
          {result.title}
        </Typography>
        <Typography
          noWrap
          sx={{ color: 'text.secondary', fontSize: 13.5, fontWeight: 500, mt: 0.2 }}
        >
          {result.meta}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

function SearchSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ px: 1.2, py: 0.8 }}>
      <Typography
        sx={{
          color: 'text.disabled',
          fontSize: 11.5,
          fontWeight: 900,
          letterSpacing: '0.12em',
          mb: 0.8,
          textTransform: 'uppercase',
        }}
      >
        {title}
      </Typography>
      <Stack spacing={0.35}>{children}</Stack>
    </Box>
  );
}

function SearchLoadingRows() {
  return (
    <Stack spacing={1.2} sx={{ px: 2.4, py: 2.2 }}>
      {[0, 1, 2].map((item) => (
        <Stack key={item} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <Skeleton variant="rounded" width={42} height={42} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="42%" />
            <Skeleton width="64%" />
          </Box>
        </Stack>
      ))}
    </Stack>
  );
}

function uniqueConversations(conversations: Conversation[]) {
  const seen = new Set<string>();
  return conversations.filter((conversation) => {
    if (seen.has(conversation.id)) return false;
    seen.add(conversation.id);
    return true;
  });
}

export function GlobalSearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const granted = useAuthStore((state) => state.user?.permissions ?? []);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const term = searchValue.trim();
  const activeTerm = debouncedValue.trim();
  const canSearchAgents = hasPermission(granted, 'users.view');
  const visibility = useChannelVisibility();
  const canSearchTickets = visibility.enabled('tickets') && hasPermission(granted, 'tickets.view');
  const canSearchArticles = hasPermission(granted, 'knowledge.view');
  const canSearchWhatsApp =
    visibility.enabled('whatsapp') && hasPermission(granted, 'whatsapp.view');

  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(term), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [term]);

  const agents = useQuery({
    queryKey: ['global-search', 'agents', activeTerm],
    queryFn: () => helixApi.users({ q: activeTerm, pageSize: MAX_RESULTS_PER_GROUP }),
    enabled: open && Boolean(activeTerm) && canSearchAgents,
    retry: false,
    staleTime: 30_000,
  });
  const tickets = useQuery({
    queryKey: ['global-search', 'tickets', activeTerm],
    queryFn: () =>
      helixApi.legacyTickets({ q: activeTerm, page: 1, pageSize: MAX_RESULTS_PER_GROUP }),
    enabled: open && Boolean(activeTerm) && canSearchTickets,
    retry: false,
    staleTime: 30_000,
  });
  const articles = useQuery({
    queryKey: ['global-search', 'articles', activeTerm],
    queryFn: () => helixApi.articles({ q: activeTerm, page: 1, pageSize: MAX_RESULTS_PER_GROUP }),
    enabled: open && Boolean(activeTerm) && canSearchArticles,
    retry: false,
    staleTime: 30_000,
  });
  const whatsApp = useQuery({
    queryKey: ['global-search', 'whatsapp', activeTerm],
    queryFn: async () => {
      const [active, ended] = await Promise.all([
        helixApi.conversations({
          channel: 'whatsapp',
          q: activeTerm,
          queue: 'active',
          pageSize: MAX_RESULTS_PER_GROUP,
        }),
        helixApi.conversations({
          channel: 'whatsapp',
          q: activeTerm,
          queue: 'ended',
          pageSize: MAX_RESULTS_PER_GROUP,
        }),
      ]);
      return uniqueConversations([...active.items, ...ended.items]).slice(0, MAX_RESULTS_PER_GROUP);
    },
    enabled: open && Boolean(activeTerm) && canSearchWhatsApp,
    retry: false,
    staleTime: 30_000,
  });

  const groups = useMemo<SearchGroup[]>(() => {
    const values: SearchGroup[] = [];
    const agentResults = (agents.data?.items ?? []).map((agent: IdentityUser) => ({
      id: `agent-${agent.id}`,
      kind: 'agent' as const,
      title: agent.displayName,
      meta: [agent.email, ...agent.roleNames].filter(Boolean).join(' · '),
      location: `/users?q=${encodeURIComponent(agent.email || agent.displayName)}`,
    }));
    const ticketResults = (tickets.data?.items ?? []).map((ticket: LegacyTicketListItem) => ({
      id: `ticket-${ticket.id}`,
      kind: 'ticket' as const,
      title: ticket.subject,
      meta: [`TCK-${ticket.id}`, ticket.customerName, ticket.customerMobile, ticket.status]
        .filter(Boolean)
        .join(' · '),
      location: `/tickets/${encodeURIComponent(String(ticket.id))}`,
    }));
    const articleResults = (articles.data?.items ?? []).map((article: KnowledgeArticle) => ({
      id: `article-${article.id}`,
      kind: 'article' as const,
      title: article.title,
      meta: [
        article.id,
        article.category,
        article.language?.toUpperCase(),
        t(`knowledge.statuses.${article.status}`),
      ]
        .filter(Boolean)
        .join(' · '),
      location: `/knowledge-base/articles/${encodeURIComponent(article.id)}`,
    }));
    const whatsAppResults = (whatsApp.data ?? []).map((conversation: Conversation) => ({
      id: `whatsapp-${conversation.id}`,
      kind: 'whatsapp' as const,
      title: conversation.customerName,
      meta: [conversation.customerId, conversation.team, conversation.slaLabel]
        .filter(Boolean)
        .join(' · '),
      location: `/inbox/whatsapp/${encodeURIComponent(conversation.id)}?queue=${
        conversation.status === 'resolved' ? 'ended' : 'active'
      }`,
    }));
    if (agentResults.length)
      values.push({ key: 'agent', title: t('shell.agents'), results: agentResults });
    if (canSearchTickets && ticketResults.length)
      values.push({ key: 'ticket', title: t('shell.tickets'), results: ticketResults });
    if (articleResults.length)
      values.push({ key: 'article', title: t('shell.knowledgeArticles'), results: articleResults });
    if (canSearchWhatsApp && whatsAppResults.length)
      values.push({ key: 'whatsapp', title: t('shell.whatsAppChats'), results: whatsAppResults });
    return values;
  }, [
    agents.data?.items,
    articles.data?.items,
    t,
    tickets.data?.items,
    whatsApp.data,
    canSearchTickets,
    canSearchWhatsApp,
  ]);
  const results = useMemo(() => groups.flatMap((group) => group.results), [groups]);
  const waitingForDebounce = Boolean(term) && term !== activeTerm;
  const isSearching =
    waitingForDebounce ||
    agents.isFetching ||
    tickets.isFetching ||
    articles.isFetching ||
    whatsApp.isFetching;
  const hasError = agents.isError || tickets.isError || articles.isError || whatsApp.isError;
  const hasEnabledSource =
    canSearchAgents || canSearchTickets || canSearchArticles || canSearchWhatsApp;

  const close = () => {
    onClose();
    setSearchValue('');
    setDebouncedValue('');
    setSelectedIndex(0);
  };
  const openResult = (result: SearchResult) => {
    close();
    void navigate(result.location);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={close}
      slotProps={{
        paper: {
          dir: i18n.language === 'ar' ? 'rtl' : 'ltr',
          sx: {
            borderRadius: '0 0 26px 26px',
            boxShadow: '0 28px 80px rgba(15, 23, 42, 0.28)',
            bgcolor: 'background.paper',
            m: 0,
            maxHeight: 'calc(100dvh - 22px)',
            position: 'absolute',
            top: 0,
          },
        },
        backdrop: { sx: { bgcolor: 'rgba(15, 23, 42, 0.28)' } },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            gap: 1.25,
            minHeight: 78,
            px: 2,
          }}
        >
          <SearchRoundedIcon sx={{ color: 'text.secondary', fontSize: 30 }} />
          <InputBase
            inputRef={inputRef}
            fullWidth
            inputProps={{ 'aria-label': t('shell.search') }}
            placeholder={t(
              canSearchTickets && canSearchWhatsApp
                ? 'shell.searchPlaceholder'
                : 'channelAdmin.searchPlaceholder',
            )}
            value={searchValue}
            onChange={(event) => {
              setSearchValue(event.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' && results.length) {
                event.preventDefault();
                setSelectedIndex((index) => (index + 1) % results.length);
              } else if (event.key === 'ArrowUp' && results.length) {
                event.preventDefault();
                setSelectedIndex((index) => (index - 1 + results.length) % results.length);
              } else if (event.key === 'Enter' && term) {
                event.preventDefault();
                close();
                void navigate(`/search?q=${encodeURIComponent(term)}`);
              }
            }}
            sx={{
              color: 'text.primary',
              flex: 1,
              fontSize: { xs: 18, sm: 24 },
              fontWeight: 600,
              letterSpacing: '-0.03em',
              '& input::placeholder': { color: 'text.secondary', opacity: 1 },
            }}
          />
          <SearchShortcutHint label="ESC" />
        </Stack>
        {isSearching ? <LinearProgress /> : <Box sx={{ height: 4 }} />}
        <Box
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            color: 'text.secondary',
            fontSize: 14,
            px: 2,
            py: 1.25,
          }}
        >
          <Box component="span" sx={{ color: 'text.primary', fontWeight: 800 }}>
            {term ? t('shell.searchingAcross') : t('shell.searchHintTitle')}
          </Box>{' '}
          {term ? t('shell.searchingAcrossHint', { query: term }) : t('shell.searchHint')}
        </Box>
        <Box
          role="listbox"
          sx={{ minHeight: 190, maxHeight: '62dvh', overflowY: 'auto', px: 1.6, py: 1.4 }}
        >
          {!term ? (
            <Box sx={{ color: 'text.secondary', py: 5, textAlign: 'center' }}>
              <SearchRoundedIcon sx={{ fontSize: 38, mb: 1, opacity: 0.45 }} />
              <Typography>{t('shell.startTyping')}</Typography>
            </Box>
          ) : isSearching && !results.length ? (
            <SearchLoadingRows />
          ) : (
            <>
              {hasError ? (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {t('shell.partialSearchError')}
                </Alert>
              ) : null}
              {groups.map((group) => (
                <SearchSection key={group.key} title={group.title}>
                  {group.results.map((result) => {
                    const index = results.findIndex((candidate) => candidate.id === result.id);
                    return (
                      <SearchResultRow
                        key={result.id}
                        result={result}
                        selected={index === selectedIndex}
                        onMouseEnter={() => setSelectedIndex(index)}
                        onClick={() => openResult(result)}
                      />
                    );
                  })}
                </SearchSection>
              ))}
              {!isSearching && !results.length ? (
                <Box sx={{ color: 'text.secondary', py: 5, textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 700 }}>
                    {hasEnabledSource ? t('shell.noSearchResults') : t('shell.noSearchAccess')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t('shell.tryAnotherSearch')}
                  </Typography>
                </Box>
              ) : null}
            </>
          )}
        </Box>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
            color: 'text.secondary',
            fontSize: 13,
            px: 2,
            py: 1.2,
          }}
        >
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <SearchShortcutHint label="↑↓" />
            <Box component="span">{t('shell.navigate')}</Box>
          </Stack>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <SearchShortcutHint label="↵" />
            <Box component="span">{t('shell.viewAllResults')}</Box>
          </Stack>
          <Box component="span">{t('shell.clickResultToOpen')}</Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
